import { Box } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import {
  getLastShownIndex,
  hasAiComment,
  isBlockCoachFieldFilled,
} from "logic/logics";
import { useMemo, useState } from "react";

import { useRecoilState, useRecoilValue } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import { userAtom } from "recoil/userAtom";
import { theme } from "styles/theme";
import ProgressBarHighlight from "./ProgressbarHighlight";

export const rainbowGradient =
  "linear-gradient(90deg, rgba(251,63,63,1) 0%, rgba(124,211,84,1) 71%, rgba(0,110,227,1) 100%)";

export default function TaskProgressBar({
  taskKey,
  data,
  setMoveToIndex,
  lastDoneAt,
}: {
  taskKey: string;
  data?: ProgramContentType[];
  setMoveToIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  lastDoneAt?: string;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const memoized = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        progress: 0,
        commentIndexList: undefined as number[] | undefined,
        highlightIndexList: undefined as number[] | undefined,
        coachAlertIndexList: undefined as number[] | undefined,
        presetHighlightIndexList: undefined as number[] | undefined,
      };
    }

    const lastIndex = getLastShownIndex(data);
    const progress = getPercentage(lastIndex + 1);

    const temp = data?.map(({ blockId, lines, isHighlight, isHidden, indentation }, index) => ({
      blockId: blockId,
      lines: lines,
      index: index,
      isHighlight: isHighlight,
      isHidden: isHidden,
      indentation: indentation,
    }));
    const temp_coach = data?.map((each, index) => ({
      index: index,
      isHighlight: !isBlockCoachFieldFilled(each),
      isHidden: each.isHidden,
    }));
    const temp_preset = data?.map((each, index) => ({
      index: index,
      isHighlight: each.lines
        .flat()
        .some((each) => ["preset", "reference"].includes(each.type)),
      isHidden: each.isHidden,
    }));

    const commentIndexList = temp
      ?.filter(
        (element) =>
          !element.isHidden &&
          element.indentation &&
          !element.isHighlight &&
          element.blockId !== "thoughtRecord" && // ExtractFromThoughtRecordCell
          element.lines
            .flat()
            .some((it: any) => (it as any)?.content?.coach === true)
      )
      .map((each) => each.index) as number[] | undefined;

    const highlightIndexList = temp
      ?.filter((element) => element.isHighlight && !element.isHidden)
      .map((each) => each.index) as number[] | undefined;

    const coachAlertIndexList = temp_coach
      ?.filter((element) => element.isHighlight && !element.isHidden)
      .map((each) => each.index) as number[] | undefined;

    const presetHighlightIndexList = (
      temp_preset
        ?.filter((element) => element.isHighlight && !element.isHidden)
        .map((each) => each.index) || []
    ).concat(
      temp
        ?.filter((element) => element.isHighlight && hasAiComment(element))
        .map((each) => each.index) || []
    ) as number[] | undefined;

    return {
      progress,
      commentIndexList,
      highlightIndexList,
      coachAlertIndexList,
      presetHighlightIndexList,
    };
  }, [data]);

  const { progress, commentIndexList, highlightIndexList, coachAlertIndexList, presetHighlightIndexList } = memoized;

  const [permanentDataState, setPermanentDataState] =
    useRecoilState(permanentDataAtom);

  const highlightWidth = `${100 / (data || []).length}%`;

  function getPercentage(index: number) {
    if (data) {
      return (index / data.length) * 100;
    }
    return 0;
  }


  const [whileHovering, setWhileHovering] = useState<boolean>(false);

  const showIndicatorAnimation =
    !permanentDataState?.isHighlghtIndicatorClicked;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "4px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          width: "100%",
          height: whileHovering ? "12px" : "4px",
          backgroundColor: theme.vars.palette.Surface.Backward,
        }}
        onMouseEnter={() => {
          setWhileHovering(true);
        }}
        onMouseLeave={() => {
          setWhileHovering(false);
        }}
        onTouchStart={() => {
          setWhileHovering(true);
        }}
        onTouchEnd={() => {
          setWhileHovering(false);
        }}
        onTouchCancel={() => {
          setWhileHovering(false);
        }}
        className="transition-all"
      >
        {progress > 0 && lastDoneAt === null ? (
          <Box
            sx={{
              position: "absolute",
              width: `${progress}%`,
              height: "100%",
              backgroundColor: theme.vars.palette.Content.SuperWeak,
            }}
            className="transition-all duration-500"
          ></Box>
        ) : (
          progress === 100 &&
          lastDoneAt && (
            <Box
              sx={{
                position: "absolute",
                width: `100%`,
                height: "100%",
                backgroundColor: theme.vars.palette.Surface.Dead,
              }}
              className="transition-all duration-500"
            ></Box>
          )
        )}
        {data && data.length > 0 && commentIndexList && (
          <ProgressBarHighlight
            taskKey={taskKey}
            data={data}
            highlightIndexList={commentIndexList}
            highlightColor={theme.vars.palette.Content.SuperWeak}
            showNumberIndicator={false}
            onClick={(highlightIndex) => {
              setMoveToIndex(highlightIndex);
              setWhileHovering(false);
            }}
          />
        )

        }
        {data && data.length > 0 && highlightIndexList && (
          <ProgressBarHighlight
            taskKey={taskKey}
            data={data}
            highlightIndexList={highlightIndexList}
            highlightColor={"primary.solid"}
            showNumberIndicator={true}
            onClick={(highlightIndex) => {
              setMoveToIndex(highlightIndex);
              setWhileHovering(false);
            }}
          />
        )}

        {isCoach && data && data.length > 0 && presetHighlightIndexList && (
          <ProgressBarHighlight
            taskKey={taskKey}
            data={data}
            highlightIndexList={presetHighlightIndexList}
            highlightGradientColor={rainbowGradient}
            onClick={(highlightIndex) => {
              setMoveToIndex(highlightIndex);
              setWhileHovering(false);
            }}
          />
        )}

        {isCoach && data && data.length > 0 && coachAlertIndexList && (
          <ProgressBarHighlight
            taskKey={taskKey}
            data={data}
            highlightIndexList={coachAlertIndexList}
            highlightColor={theme.vars.palette.Content.HighlightWithAlert}
            onClick={(highlightIndex) => {
              setMoveToIndex(highlightIndex);
              setWhileHovering(false);
            }}
          />
        )}
      </Box>
    </Box>
  );
}
